import db from "../utils/firestore";
import firebase from "firebase/compat";
import {
    ExperienceLevelType,
    IExperienceLevelOption,
    UpdatedFrom,
} from "intake_shared/src/types";
import { Collection } from "intake_shared/src/enum";
import { arrayUnion } from "firebase/firestore";

// function to determine if the levels are the same
function areLevelsEqual(
    level1: IExperienceLevelOption,
    level2: IExperienceLevelOption
): boolean {
    return (
        level1.category === level2.category &&
        level1.type === level2.type &&
        level1.value === level2.value
    );
}

export async function getAllExperienceLevels(): Promise<IExperienceLevelOption[]> {
    const experience_levels: IExperienceLevelOption[] = [];
    const experience_levels_raw = await await db
        .collection(Collection.EXPERIENCELEVELS)
        .doc("options")
        .get();
    if (experience_levels_raw.exists) {
        experience_levels.push(...(experience_levels_raw.data()?.options || []));
    }
    return experience_levels;
}

// function to delete experience levels
export async function deleteExperienceLevels(
    levelToDelete: IExperienceLevelOption
): Promise<boolean> {
    const experienceLevelsRef = db
        .collection(Collection.EXPERIENCELEVELS)
        .doc("options");
    const cliniciansCollection = db.collection(Collection.CLINICIANS);
    const batch = db.batch();
    try {
        const experienceLevelsDoc = await experienceLevelsRef.get();
        if (experienceLevelsDoc.exists) {
            const currentOptions = experienceLevelsDoc.data()?.options || [];
            const updatedOptions = currentOptions.filter(
                (level) => !areLevelsEqual(level, levelToDelete)
            );
            // Update the options in the experience levels document
            batch.update(experienceLevelsRef, { options: updatedOptions });

            // checking through all clinician documents and deleting the experience level
            const cliniciansQuerySnapshot = await cliniciansCollection
                .where(`${levelToDelete.value}`, "in", [true, false])
                .get();
            cliniciansQuerySnapshot.forEach((clinicianDoc) => {
                const clinicianId = clinicianDoc.id;
                const clinicianData = clinicianDoc.data();
                if (
                    clinicianData.experience_levels &&
                    clinicianData.experience_levels[levelToDelete.value]
                ) {
                    const fieldNameToRemove = levelToDelete.value;
                    if (clinicianData.experience_levels[fieldNameToRemove]) {
                        // Delete the specific field from the clinician document
                        const updateObj: Record<string, any> = {};
                        updateObj[fieldNameToRemove] =
                            firebase.firestore.FieldValue.delete();
                        batch.update(
                            cliniciansCollection.doc(clinicianId),
                            updateObj
                        );
                    }
                    // Delete the experience level from the clinician document
                    delete clinicianData.experience_levels[levelToDelete.value];

                    batch.update(cliniciansCollection.doc(clinicianId), {
                        experience_levels: clinicianData.experience_levels,
                        updated_from: UpdatedFrom.INTAKE,
                    });
                }
            });
            await batch.commit();
            return true;
        } else {
            return false;
        }
    } catch (error) {
        throw error;
    }
}

export async function getExperienceLevelAcrossClinicians() {
    const clinician_collection = db.collection(Collection.CLINICIANS);
    const all_clinicians = await (await clinician_collection.get()).docs;
    const matched_clinicians: { [key: string]: any[] } = {};
    for (const clinician of all_clinicians) {
        const clinician_data = clinician.data();
        for (const exp_level_name in clinician_data.experience_levels) {
            const clinician_level_details = {
                email: clinician_data.email,
                level_details: clinician_data.experience_levels[exp_level_name],
                locations: clinician_data.locations,
            };
            if (matched_clinicians[exp_level_name]) {
                matched_clinicians[exp_level_name].push(clinician_level_details);
            } else {
                matched_clinicians[exp_level_name] = [clinician_level_details];
            }
        }
    }
    return { matched_clinicians, clinician_count: all_clinicians.length };
}

export async function addNewExperienceLevel(
    category: string,
    type: ExperienceLevelType,
    value: string,
    credential: string,
    clinician_hidden: boolean,
    certification_toogle: boolean
) {
    await db
        .collection(Collection.EXPERIENCELEVELS)
        .doc("options")
        .update({
            options: arrayUnion({
                category,
                type,
                value,
                credential,
                clinician_hidden,
                show: true,
                certification_toogle,
            }),
        });
}
